import { useState, useEffect } from 'react';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { useParams } from 'react-router';
import { PATH_DASHBOARD } from 'src/routes/paths';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { getAgeYearsAndMonths } from 'src/utils/formatTime';
import ExpandableCard from '../../../../../components/common/ExpandableCard';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import CustomLink from 'src/careflow/common/CustomLink';
import { CaseService } from 'src/crs/case/service/CaseService';

interface FamilyMembersProps {
  isOpen?: boolean;
  carePlanId?: string;
}

const FamilyMembers = ({ isOpen = false, carePlanId: propCarePlanId }: FamilyMembersProps) => {
  const { i18n } = useLocales();
  const { id: urlCarePlanId } = useParams();

  // Use prop carePlanId if provided, otherwise fall back to URL param
  const carePlanId = propCarePlanId || urlCarePlanId;

  const [familyMembers, setFamilyMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchFamilyMembers = async () => {
      if (!carePlanId) {
        return;
      }

      setLoading(true);
      try {
        const members = await CaseService.getFamilyMembers(carePlanId);
        setFamilyMembers(members || []);
      } catch (error) {
        setFamilyMembers([]);
      } finally {
        setLoading(false);
      }
    };

    if (carePlanId) {
      fetchFamilyMembers();
    }
  }, [carePlanId]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatAge = (birthDate: string) => {
    if (!birthDate) return '';
    return getAgeYearsAndMonths(birthDate);
  };

  if (!checkAclValidation({ acls: [crsAcls.CRS.CASE.FAMILY_MEMBERS.VIEW] })) {
    return null;
  }

  return (
    <ExpandableCard
      title={i18n('case.details.familyMembers.title', 'crs')}
      openCollapseExternal={isOpen}
    >
      <Card sx={{ p: 2, boxShadow: 'none' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <Typography variant="body2" color="text.secondary">
              Loading family members...
            </Typography>
          </Box>
        ) : familyMembers.length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>{i18n('patients.details.groups.mrn', 'crs')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{i18n('patients.details.groups.fullname', 'crs')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{i18n('patients.dateOfBirth', 'crs')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{i18n('patients.details.groups.age', 'crs')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{i18n('case.details.familyMembers.relationship', 'crs')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {familyMembers.map((member, index) => {
                const mrnValue = (() => {
                  if (member.identifier && Array.isArray(member.identifier)) {
                    const mrnPatterns = [
                      (id: any) => id.system?.includes('mrn') || id.system?.includes('MRN'),
                      (id: any) => id.type?.coding?.[0]?.system?.includes('mrn') || id.type?.coding?.[0]?.code === 'MR',
                      (id: any) => id.system?.includes('medical') && id.system?.includes('record'),
                      (id: any) => id.value && /^[A-Z0-9-]+$/.test(id.value) && id.value.length > 5
                    ];

                    for (const pattern of mrnPatterns) {
                      const mrnId = member.identifier.find(pattern);
                      if (mrnId) {
                        return mrnId.value || 'N/A';
                      }
                    }
                  }

                  return 'N/A';
                })();

                return (
                  <TableRow key={member.id || index} hover sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell>
                      {member.id ? (
                        <CustomLink
                          to={PATH_DASHBOARD.crs.patient.details.forId(member.id)}
                          target="_blank"
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#1976d2', // Material-UI blue color
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              '&:hover': {
                                color: '#1565c0', // Darker blue on hover
                              }
                            }}
                          >
                            {mrnValue}
                          </Typography>
                        </CustomLink>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {mrnValue}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {member.name?.[0]?.given?.join(' ')} {member.name?.[0]?.family}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(member.birthDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatAge(member.birthDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label="Family"
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <Typography variant="body2" color="text.secondary">
              {i18n('case.details.familyMembers.noMembers', 'crs')}
            </Typography>
          </Box>
        )}
      </Card>
    </ExpandableCard>
  );
};

export default FamilyMembers;
